import { useState, type ReactNode } from 'react'
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Brain,
  Check,
  CheckCircle2,
  Copy,
  FileSearch,
  Link2,
  Loader2,
  RefreshCw,
  SearchCheck,
  ShieldAlert,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import type {
  CashflowForecastResponse,
  FinancialAIEnvelope,
  FinancialStatementResponse,
  ReconciliationLookup,
  ReconciliationResponse,
} from '../services/financialIntelligence'

interface BaseModuleCardProps<TData> {
  data?: FinancialAIEnvelope<TData>
  isLoading: boolean
  isFetching?: boolean
  error?: unknown
  onRetry?: () => void
  title?: string
  subtitle?: string
}

export interface ReconciliationModuleCardProps
  extends BaseModuleCardProps<ReconciliationResponse> {
  lookup?: ReconciliationLookup
}

export interface CashflowModuleCardProps
  extends BaseModuleCardProps<CashflowForecastResponse> {
  showAmounts?: boolean
}

export interface StatementModuleCardProps
  extends BaseModuleCardProps<FinancialStatementResponse> {}

interface FinancialIntelligencePanelProps {
  title?: string
  description?: string
  reconciliation?: ReconciliationModuleCardProps
  cashflow?: CashflowModuleCardProps
  statement?: StatementModuleCardProps
  showAmounts?: boolean
}

const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

function formatCurrency(value: number, showAmounts = true) {
  if (!showAmounts) return '€ •••••'
  return currencyFormatter.format(value)
}

function formatConfidence(confidence: number) {
  return `${Math.round(confidence * 100)}%`
}

function getConfidenceTone(confidence: number) {
  if (confidence >= 0.8) {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
  }
  if (confidence >= 0.6) {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
  }
  return 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
}

function getBalanceTone(balance: number) {
  return balance >= 0
    ? 'text-emerald-600 dark:text-emerald-300'
    : 'text-red-600 dark:text-red-300'
}

function toErrorMessage(error: unknown) {
  if (!error) return 'Errore sconosciuto'
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  return 'Impossibile caricare il modulo AI'
}

function localizeAiText(text: string) {
  return text
    .replace(/previous /g, 'precedente ')
    .replace(/YoY /g, 'Var. annua ')
    .replace(
      'baseline moving-average daily net cash trend',
      'trend baseline della media mobile del cashflow netto giornaliero',
    )
    .replace(
      'RunPod TFT endpoint not wired in this environment; baseline fallback returned.',
      'Endpoint RunPod TFT non ancora collegato in questo ambiente; restituito fallback baseline.',
    )
    .replace(
      'Negative projected balance detected in at least one horizon.',
      'Saldo previsto negativo rilevato in almeno un orizzonte.',
    )
    .replace(
      'One or more forecast horizons require human review.',
      'Uno o piu orizzonti previsionali richiedono revisione umana.',
    )
    .replace(
      'DeepSeek/AutoRAG runtime integration pending; deterministic structured analysis returned.',
      'Integrazione runtime DeepSeek/AutoRAG ancora pendente; restituita analisi strutturata deterministica.',
    )
}

function CopyTextButton({
  value,
  label = 'Copia',
}: {
  value: string
  label?: string
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1400)
    } catch {
      setCopied(false)
    }
  }

  return (
    <button
      type="button"
      onClick={() => {
        void handleCopy()
      }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-[11px] font-medium text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
      title={label}
      aria-label={label}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copiato' : 'Copia'}
    </button>
  )
}

function ModuleShell(props: {
  icon: ReactNode
  title: string
  subtitle: string
  badge?: ReactNode
  isFetching?: boolean
  copyText?: string
  children: ReactNode
}) {
  const { icon, title, subtitle, badge, isFetching, copyText, children } = props

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/70 p-5 h-full">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {icon}
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{title}</p>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {isFetching && (
            <span className="badge bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Aggiorna
            </span>
          )}
          {copyText && <CopyTextButton value={copyText} label={`Copia ${title.toLowerCase()}`} />}
          {badge}
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  )
}

function ModuleLoadingState({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: lines }).map((_, index) => (
        <div key={index} className="skeleton h-12 rounded-xl" />
      ))}
    </div>
  )
}

function ModuleErrorState({
  message,
  onRetry,
}: {
  message: string
  onRetry?: () => void
}) {
  return (
    <div className="rounded-xl border border-dashed border-red-200 dark:border-red-900/40 bg-red-50/70 dark:bg-red-950/20 p-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-800 dark:text-red-300">
            Modulo non disponibile
          </p>
          <p className="text-xs text-red-700/80 dark:text-red-400/80 mt-1">{message}</p>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg bg-white dark:bg-slate-800 border border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Riprova
          </button>
        )}
      </div>
    </div>
  )
}

function ModuleEmptyState({
  icon,
  title,
  body,
}: {
  icon: ReactNode
  title: string
  body: string
}) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/30 p-4">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center">
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-900 dark:text-white">{title}</p>
          <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">{body}</p>
        </div>
      </div>
    </div>
  )
}

function WarningChips({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {warnings.slice(0, 4).map((warning, index) => (
        <span
          key={`${warning}-${index}`}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
        >
          <AlertTriangle className="w-3 h-3" />
          {localizeAiText(warning)}
        </span>
      ))}
    </div>
  )
}

export function ReconciliationModuleCard(props: ReconciliationModuleCardProps) {
  const {
    data,
    lookup,
    isLoading,
    isFetching,
    error,
    onRetry,
    title = 'Riconciliazione AI',
    subtitle = 'Suggerimenti di matching da transazioni e fatture reali.',
  } = props

  const summaryCopyText =
    data?.data.suggestions
      .slice(0, 3)
      .map((suggestion) => {
        const transactionLabel =
          lookup?.transactions[suggestion.transaction_id] || suggestion.transaction_id
        const invoiceLabel = lookup?.invoices[suggestion.invoice_id] || suggestion.invoice_id
        return [
          `Transazione: ${transactionLabel}`,
          `Fattura: ${invoiceLabel}`,
          `Score: ${Math.round(suggestion.score * 100)}%`,
          `Spiegazione: ${localizeAiText(suggestion.user_explanation)}`,
        ].join('\n')
      })
      .join('\n\n') ?? ''

  return (
    <ModuleShell
      icon={<Link2 className="w-4 h-4 text-sky-500" />}
      title={title}
      subtitle={subtitle}
      isFetching={isFetching}
      copyText={summaryCopyText}
      badge={
        data?.audit && (
          <span className={`badge ${getConfidenceTone(data.audit.confidence)}`}>
            {formatConfidence(data.audit.confidence)}
          </span>
        )
      }
    >
      {isLoading ? (
        <ModuleLoadingState lines={4} />
      ) : error ? (
        <ModuleErrorState message={toErrorMessage(error)} onRetry={onRetry} />
      ) : !data?.data.suggestions.length ? (
        <ModuleEmptyState
          icon={<SearchCheck className="w-4 h-4 text-slate-500" />}
          title="Nessun candidato disponibile"
          body="Servono almeno fatture aperte e movimenti bancari recenti per produrre suggerimenti."
        />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-slate-50 dark:bg-slate-700/40 p-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                Coppie
              </p>
              <p className="text-xl font-semibold text-slate-900 dark:text-white mt-1">
                {data.data.suggestions.length}
              </p>
            </div>
            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/20 p-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-emerald-600 dark:text-emerald-300">
                Automatici
              </p>
              <p className="text-xl font-semibold text-emerald-700 dark:text-emerald-300 mt-1">
                {
                  data.data.suggestions.filter(
                    (suggestion) => suggestion.status === 'auto_match',
                  ).length
                }
              </p>
            </div>
            <div className="rounded-xl bg-amber-50 dark:bg-amber-950/20 p-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-amber-600 dark:text-amber-300">
                Revisione
              </p>
              <p className="text-xl font-semibold text-amber-700 dark:text-amber-300 mt-1">
                {
                  data.data.suggestions.filter(
                    (suggestion) => suggestion.status !== 'auto_match',
                  ).length
                }
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {data.data.suggestions.slice(0, 3).map((suggestion) => {
              const transactionLabel =
                lookup?.transactions[suggestion.transaction_id] || suggestion.transaction_id
              const invoiceLabel =
                lookup?.invoices[suggestion.invoice_id] || suggestion.invoice_id

              return (
                <div
                  key={`${suggestion.transaction_id}-${suggestion.invoice_id}`}
                  className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {transactionLabel}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {invoiceLabel}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CopyTextButton
                        value={[
                          `Transazione: ${transactionLabel}`,
                          `Fattura: ${invoiceLabel}`,
                          `Score: ${Math.round(suggestion.score * 100)}%`,
                          `Spiegazione: ${localizeAiText(suggestion.user_explanation)}`,
                        ].join('\n')}
                        label="Copia suggerimento"
                      />
                      <span className={`badge ${getConfidenceTone(suggestion.confidence)}`}>
                        Score {Math.round(suggestion.score * 100)}%
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-3">
                    {localizeAiText(suggestion.user_explanation)}
                  </p>
                </div>
              )
            })}
          </div>

          <WarningChips warnings={data.data.warnings} />
        </div>
      )}
    </ModuleShell>
  )
}

export function CashflowModuleCard(props: CashflowModuleCardProps) {
  const {
    data,
    isLoading,
    isFetching,
    error,
    onRetry,
    title = 'Previsione di cassa AI',
    subtitle = 'Proiezioni di cassa multi-orizzonte con eventi futuri noti.',
    showAmounts = true,
  } = props

  const forecast = data?.data
  const horizons = forecast?.horizons ?? []
  const spotlight = horizons.find((horizon) => horizon.horizon_days === 30) ?? horizons[0]
  const keyHorizons = [7, 30, 90]
    .map((days) => horizons.find((horizon) => horizon.horizon_days === days))
    .filter((horizon): horizon is NonNullable<typeof horizon> => Boolean(horizon))
  const summaryCopyText = spotlight
    ? [
        `Bilancio a ${spotlight.horizon_days} giorni: ${formatCurrency(
          spotlight.projected_balance,
          showAmounts,
        )}`,
        `Intervallo: ${formatCurrency(spotlight.lower_bound, showAmounts)} - ${formatCurrency(
          spotlight.upper_bound,
          showAmounts,
        )}`,
        `Confidenza: ${formatConfidence(spotlight.confidence)}`,
        ...spotlight.drivers.slice(0, 3).map((driver) => `Driver: ${localizeAiText(driver)}`),
      ].join('\n')
    : ''

  return (
    <ModuleShell
      icon={<TrendingUp className="w-4 h-4 text-emerald-500" />}
      title={title}
      subtitle={subtitle}
      isFetching={isFetching}
      copyText={summaryCopyText}
      badge={
        data?.audit && (
          <span className={`badge ${getConfidenceTone(data.audit.confidence)}`}>
            {formatConfidence(data.audit.confidence)}
          </span>
        )
      }
    >
      {isLoading ? (
        <ModuleLoadingState lines={4} />
      ) : error ? (
        <ModuleErrorState message={toErrorMessage(error)} onRetry={onRetry} />
      ) : !spotlight ? (
        <ModuleEmptyState
          icon={<Activity className="w-4 h-4 text-slate-500" />}
          title="Serie storica insufficiente"
          body="Servono osservazioni giornaliere recenti per generare una previsione di cassa attendibile."
        />
      ) : (
        <div className="space-y-4">
          <div className="rounded-2xl bg-slate-50 dark:bg-slate-700/40 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                  Bilancio a {spotlight.horizon_days} giorni
                </p>
                <p
                  className={`text-2xl font-semibold mt-1 ${getBalanceTone(
                    spotlight.projected_balance,
                  )}`}
                >
                  {formatCurrency(spotlight.projected_balance, showAmounts)}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                    Intervallo
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-200 mt-1">
                    {formatCurrency(spotlight.lower_bound, showAmounts)} -{' '}
                    {formatCurrency(spotlight.upper_bound, showAmounts)}
                  </p>
                </div>
                <CopyTextButton
                  value={[
                    `Bilancio a ${spotlight.horizon_days} giorni: ${formatCurrency(
                      spotlight.projected_balance,
                      showAmounts,
                    )}`,
                    `Intervallo: ${formatCurrency(
                      spotlight.lower_bound,
                      showAmounts,
                    )} - ${formatCurrency(spotlight.upper_bound, showAmounts)}`,
                    `Confidenza: ${formatConfidence(spotlight.confidence)}`,
                  ].join('\n')}
                  label="Copia previsione principale"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(keyHorizons.length > 0 ? keyHorizons : horizons.slice(0, 3)).map((horizon) => (
              <div
                key={horizon.horizon_days}
                className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"
              >
                <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                  {horizon.horizon_days} giorni
                </p>
                <p
                  className={`text-lg font-semibold mt-1 ${getBalanceTone(
                    horizon.projected_balance,
                  )}`}
                >
                  {formatCurrency(horizon.projected_balance, showAmounts)}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Confidenza {formatConfidence(horizon.confidence)}
                </p>
                <div className="mt-3">
                  <CopyTextButton
                    value={[
                      `${horizon.horizon_days} giorni`,
                      `Bilancio previsto: ${formatCurrency(
                        horizon.projected_balance,
                        showAmounts,
                      )}`,
                      `Confidenza: ${formatConfidence(horizon.confidence)}`,
                    ].join('\n')}
                    label={`Copia orizzonte ${horizon.horizon_days} giorni`}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {spotlight.drivers.slice(0, 3).map((driver, index) => (
              <span
                key={`${driver}-${index}`}
                className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
              >
                {localizeAiText(driver)}
              </span>
            ))}
            {forecast?.baseline_used && (
              <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                Fallback baseline
              </span>
            )}
          </div>

          <WarningChips warnings={forecast?.warnings ?? []} />
        </div>
      )}
    </ModuleShell>
  )
}

export function StatementAnalysisCard(props: StatementModuleCardProps) {
  const {
    data,
    isLoading,
    isFetching,
    error,
    onRetry,
    title = 'Analisi di bilancio AI',
    subtitle = 'Lettura executive e contabile dei KPI del periodo selezionato.',
  } = props

  const summaryCopyText = data
    ? [
        ...data.data.executive_summary.bullets.slice(0, 3).map((item) => localizeAiText(item)),
        ...data.data.warning_signals.slice(0, 2).map((item) => localizeAiText(item)),
        ...data.data.accountant_analysis.bullets
          .slice(0, 2)
          .map((item) => localizeAiText(item)),
      ].join('\n')
    : ''

  return (
    <ModuleShell
      icon={<FileSearch className="w-4 h-4 text-violet-500" />}
      title={title}
      subtitle={subtitle}
      isFetching={isFetching}
      copyText={summaryCopyText}
      badge={
        data?.audit && (
          <span className={`badge ${getConfidenceTone(data.audit.confidence)}`}>
            {formatConfidence(data.audit.confidence)}
          </span>
        )
      }
    >
      {isLoading ? (
        <ModuleLoadingState lines={4} />
      ) : error ? (
        <ModuleErrorState message={toErrorMessage(error)} onRetry={onRetry} />
      ) : !data?.data.executive_summary.bullets.length ? (
        <ModuleEmptyState
          icon={<Sparkles className="w-4 h-4 text-slate-500" />}
          title="Analisi non generata"
          body="Servono KPI executive e tecnici coerenti per produrre la lettura del conto economico."
        />
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl bg-slate-50 dark:bg-slate-700/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                Sintesi executive
              </p>
              <CopyTextButton
                value={data.data.executive_summary.bullets
                  .slice(0, 3)
                  .map((item) => localizeAiText(item))
                  .join('\n')}
                label="Copia sintesi executive"
              />
            </div>
            <div className="space-y-2 mt-3">
              {data.data.executive_summary.bullets.slice(0, 3).map((bullet, index) => (
                <div key={`${bullet}-${index}`} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-slate-700 dark:text-slate-200">
                    {localizeAiText(bullet)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-amber-500" />
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Segnali da verificare
                  </p>
                </div>
                <CopyTextButton
                  value={(data.data.warning_signals.length > 0
                    ? data.data.warning_signals
                    : data.data.recommended_checks
                  )
                    .slice(0, 3)
                    .map((item) => localizeAiText(item))
                    .join('\n')}
                  label="Copia segnali da verificare"
                />
              </div>
              <div className="space-y-2">
                {(data.data.warning_signals.length > 0
                  ? data.data.warning_signals
                  : data.data.recommended_checks
                )
                  .slice(0, 3)
                  .map((item, index) => (
                    <p key={`${item}-${index}`} className="text-xs text-slate-600 dark:text-slate-300">
                      {localizeAiText(item)}
                    </p>
                  ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Punti di forza
                  </p>
                </div>
                <CopyTextButton
                  value={data.data.key_strengths
                    .slice(0, 3)
                    .map((item) => localizeAiText(item))
                    .join('\n')}
                  label="Copia punti di forza"
                />
              </div>
              <div className="space-y-2">
                {data.data.key_strengths.slice(0, 3).map((item, index) => (
                  <p key={`${item}-${index}`} className="text-xs text-slate-600 dark:text-slate-300">
                    {localizeAiText(item)}
                  </p>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-sky-500" />
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  Lettura contabile
                </p>
              </div>
              <CopyTextButton
                value={data.data.accountant_analysis.bullets
                  .slice(0, 3)
                  .map((item) => localizeAiText(item))
                  .join('\n')}
                label="Copia lettura contabile"
              />
            </div>
            <div className="space-y-2">
              {data.data.accountant_analysis.bullets.slice(0, 3).map((bullet, index) => (
                <p key={`${bullet}-${index}`} className="text-xs text-slate-600 dark:text-slate-300">
                  {localizeAiText(bullet)}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}
    </ModuleShell>
  )
}

export default function FinancialIntelligencePanel(props: FinancialIntelligencePanelProps) {
  const {
    title = 'Intelligenza finanziaria',
    description = 'Tre moduli AI connessi ai dati operativi della workspace attiva.',
    reconciliation,
    cashflow,
    statement,
    showAmounts = true,
  } = props

  if (!reconciliation && !cashflow && !statement) {
    return null
  }

  return (
    <div className="card" data-tour="financial-intelligence">
      <div className="card-header flex items-start justify-between gap-3">
        <div>
          <h3 className="card-title flex items-center gap-2">
            <Brain className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            {title}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{description}</p>
        </div>
        <span className="badge bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          Connesso live
        </span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {reconciliation && <ReconciliationModuleCard {...reconciliation} />}
        {cashflow && <CashflowModuleCard {...cashflow} showAmounts={showAmounts} />}
        {statement && <StatementAnalysisCard {...statement} />}
      </div>
    </div>
  )
}
